import React, { memo } from 'react';

/**
 * @file Componente para exibir o avatar de um usuário.
 * Mostra a foto do perfil do usuário ou um avatar gerado com as iniciais do nome.
 *
 * @param {object} props - As propriedades do componente.
 * @param {object} props.user - O objeto do usuário, contendo `photoURL` and `displayName`.
 * @param {string} [props.size='h-7 w-7'] - As classes de tamanho do Tailwind CSS para o avatar.
 */
const UserAvatar = ({ user, size = 'h-7 w-7' }) => {
    // Obtém a URL da foto do usuário.
    const photoURL = user?.photoURL;
    // Obtém o nome de exibição ou um fallback.
    const displayName = user?.displayName || 'Usuário Desconhecido';
    // Gera uma URL de fallback para um avatar com as iniciais, caso não haja foto.
    const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random&size=32`;

    return (
        <img
            src={photoURL || fallbackAvatar}
            alt={`Foto de ${displayName}`}
            title={displayName}
            className={`${size} rounded-full object-cover border-2 border-white dark:border-slate-800/80`}
            // Em caso de erro ao carregar a foto do perfil, usa o avatar de fallback.
            onError={(e) => { e.target.onerror = null; e.target.src = fallbackAvatar; }}
        />
    );
};

// Optimized: UserAvatar is used frequently in lists (Projects, Pendencies, Notifications).
// Memoizing it prevents unnecessary re-renders when parent lists update but user data remains the same.
// Custom comparison ensures we check relevant fields even if the user object reference changes.
export default memo(UserAvatar, (prevProps, nextProps) => {
    // If size changed, re-render
    if (prevProps.size !== nextProps.size) return false;

    // If user reference is the same, no re-render (fast path)
    if (prevProps.user === nextProps.user) return true;

    // Deep check relevant fields if references differ
    const prevUser = prevProps.user || {};
    const nextUser = nextProps.user || {};

    return (
        prevUser.uid === nextUser.uid &&
        prevUser.photoURL === nextUser.photoURL &&
        prevUser.displayName === nextUser.displayName
    );
});
